import * as React from 'react';
import * as _ from 'underscore';

import { angularProviderHOC } from '../../../common/react/AngularProviderHOC';
import { ISettingsCoordinatorCtrlScope } from '../../../settings/directives/tbSettingsCoordinator';
import { ISiteBridgeSettingsForSiteAdmin } from '../../../settings/types/ISiteSettingsForSiteAdmin';
import { AngularContext } from '../../../ts/factories/AngularContextFactory';
import { Sites } from '../../../ts/siteManagement/services/resources/Sites';
import { SessionManagementService } from '../../../sessionManagement/SessionManagementService';
import { RouterError } from '../RouterHelpers/RouterError';
import { SpacesSiteUrls } from '../SpacesSiteUrls';
import {BridgeSiteSettingsCoordinator} from '~src/settings/general-settings/components/bridge-settings/BridgeSiteSettingsCoordinator';
import {subspaceStyle} from '~src/settings/styles/CommonStyles';

export interface BridgeSiteSettingsSubspaceOwnProps {
  readonly siteName: string;
  readonly urls: SpacesSiteUrls;
  readonly angularContext: AngularContext;
  readonly server: SessionManagementService;
  readonly isSessionMultiSite: boolean;
  readonly isSessionServerAdmin: boolean;
}

export interface BridgeSiteSettingsSubspaceAngularProps {
  readonly sites: Sites;
}

export type BridgeSiteSettingsSubspaceProps = BridgeSiteSettingsSubspaceOwnProps & BridgeSiteSettingsSubspaceAngularProps;

export interface BridgeSiteSettingsSubspaceState {
  readonly hasDataToRender: boolean;
  readonly hasError: boolean;
  readonly settings: ISiteBridgeSettingsForSiteAdmin;
}

export class BridgeSiteSettingsSubspaceComponent extends React.PureComponent<BridgeSiteSettingsSubspaceProps, BridgeSiteSettingsSubspaceState> {

  private childScope: ISettingsCoordinatorCtrlScope = undefined;
  private unmounted: boolean = false;

  private initialState: BridgeSiteSettingsSubspaceState = {hasDataToRender: false, settings: undefined, hasError: false};

  public constructor(props: BridgeSiteSettingsSubspaceProps) {
    super(props);
    this.state = this.initialState;
  }

  public componentDidMount() {
    this.getData();
  }

  public componentWillUnmount() {
    this.unmounted = true;
  }

  public componentDidUpdate(prevProps: BridgeSiteSettingsSubspaceProps) {
    if (!_.isUndefined(this.props.siteName) && prevProps.siteName !== this.props.siteName) {
      this.updateState(this.initialState);
      this.getData();
      return;
    }

    if (!_.isUndefined(this.state.settings)) {
        this.updateState({hasDataToRender: true});
    }

    if (this.state.hasDataToRender && !_.isUndefined(this.childScope)) {
      _.defer(() => this.childScope.$apply());
    }
  }

  public render() {
    if (!this.state.hasDataToRender) {
      return null;
    } else if (this.state.hasError) {
      return <RouterError errorType={'default'} urls={this.props.urls} />;
    }

    return (
      <div className={subspaceStyle}>
        <BridgeSiteSettingsCoordinator {...this.state}/>
      </div>
    );
  }

  private updateState<K extends keyof BridgeSiteSettingsSubspaceState>(newState: Pick<BridgeSiteSettingsSubspaceState, K>, callback?: () => void): void {
    if (!this.unmounted) {
      this.setState(newState, callback);
    }
  }

  private getData = async () => {
    try {
      await this.props.server.getSessionInfoPromise();
      const settings = await this.props.sites.getCurrentSiteBridgeSettingsForSiteAdmin();
      this.updateState({settings});
    } catch (e) {
      this.updateState({hasError: true});
    }
  }
}

const BridgeSiteSettingsHOC = angularProviderHOC<BridgeSiteSettingsSubspaceOwnProps, BridgeSiteSettingsSubspaceAngularProps, BridgeSiteSettingsSubspaceAngularProps>({
  getAngularServicesWithServiceLocator: () => ({
    sites: Sites.instance,
  }),
  selectPropsFromAngularServices: angularServices => angularServices,
});

export const BridgeSiteSettingsSubspace: React.ComponentType<BridgeSiteSettingsSubspaceOwnProps>
  = BridgeSiteSettingsHOC(BridgeSiteSettingsSubspaceComponent);
